import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Component, computed, inject, input, signal } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular';
import * as fromSeriousSystem from '@serious-system';
import { ToastrService } from 'ngx-toastr';
import { catchError, EMPTY, finalize, Observable, tap } from 'rxjs';
import * as fromGenerated from '../../../../../_generated';
import * as fromAnalytics from '../../../../../analytics';
import * as fromFeatureFlags from '../../../../../feature-flags';
import { getFilenameFromContentDisposition } from '../../../../shared';

type ExportFormat = fromAnalytics.ExportChatbotMessageEvent['exportFormat'];

/**
 * Per-format download details; the menu rows and `request()` hold the rest.
 * `from` names an answer's export in Mixpanel's `file_downloaded`.
 */
const DOWNLOADS = {
  docx: {
    fallbackFilename: 'export.docx',
    from: 'export_docx',
    errorKey: 'CHAT.DROPDOWN_MENU.EXPORT_TO_DOCX_ERROR',
  },
  pdf: {
    fallbackFilename: 'export.pdf',
    from: 'export_pdf',
    errorKey: 'CHAT.DROPDOWN_MENU.EXPORT_TO_PDF_ERROR',
  },
} as const satisfies Record<
  ExportFormat,
  {
    fallbackFilename: string;
    from: fromAnalytics.FileDownloadedEvent['from'];
    errorKey: string;
  }
>;

/**
 * Whether the component reports an export failure to Sentry.
 * A 5xx already reaches Sentry through `httpClientIntegration` (instrument.ts),
 * so reporting it here would duplicate it; a 429 is the export throttle working
 * as designed. Everything else is reported: other 4xx, a network
 * failure (status 0), and an error thrown while saving the file.
 * An export's 503 (`EXPORT_BUSY`) still reaches Sentry through the integration,
 * by decision; keeping it out would take a `beforeSend` filter in instrument.ts.
 */
export const reportsExportFailure = (error: unknown): boolean =>
  !(error instanceof HttpErrorResponse) ||
  (error.status !== 429 && error.status < 500);

@Component({
  selector: 'squadbox-message-quick-actions-export',
  imports: [
    fromSeriousSystem.IconButtonDirective,
    fromSeriousSystem.UseUIBasicIconDirective,
    fromSeriousSystem.DropdownMenuComponent,
    fromSeriousSystem.DropdownMenuTriggerDirective,
    fromSeriousSystem.SpinnerDirective,
    TranslateModule,
    MatTooltipModule,
  ],
  template: `
    <button
      sdIconButton
      variant="icon"
      color="neutral"
      size="xs"
      [attr.aria-label]="'CHAT.DROPDOWN_MENU.EXPORT' | translate"
      [sdDropdownMenuTrigger]="exportDropdown"
      [disabled]="isLoading()"
    >
      @if (isLoading()) {
        <div sdSpinner size="sm" color="primary"></div>
      } @else {
        <svg
          matTooltip="{{ 'CHAT.DROPDOWN_MENU.EXPORT' | translate }}"
          matTooltipPosition="above"
          sdUseUIBasicIcon="file-export"
        ></svg>
      }
    </button>

    <sd-dropdown-menu
      #exportDropdown
      [options]="exportDropdownMenuOptions()"
    ></sd-dropdown-menu>
  `,
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
})
export class MessageQuickActionsExportComponent {
  private readonly featureFlagsStore =
    inject<Store<fromFeatureFlags.FeatureFlagsState>>(Store);
  private readonly translate = inject(TranslateService);
  private readonly toastrService = inject(ToastrService);
  private readonly chatsService = inject(fromGenerated.ChatsService);
  private readonly analyticsService = inject(fromAnalytics.AnalyticsService);

  /**
   * The defaulted selector, not the raw `ff-quick-actions` value: with
   * `ff-export-pdf` on, this component renders even when `ff-quick-actions` is
   * missing, and `selectQuickActions` is what merges the PDF flag in.
   */
  private readonly ffQuickActions = this.featureFlagsStore.selectSignal(
    fromFeatureFlags.featureFlagsFeature.selectQuickActions,
  );
  public readonly ffExport = computed(() => this.ffQuickActions().export);
  public readonly chatUuid = input.required<string>();
  public readonly chatId = input.required<number>();
  public readonly messageId = input.required<number>();
  public readonly isLoading = signal(false);
  public readonly exportDropdownMenuOptions = computed<
    fromSeriousSystem.DropdownMenuOption[]
  >(() => {
    const options: fromSeriousSystem.DropdownMenuOption[] = [];

    if (this.ffExport().docx) {
      options.push({
        label: this.translate.instant(
          'CHAT.DROPDOWN_MENU.EXPORT_TO_DOCX',
        ) as string,
        icon: { type: 'fileType', icon: 'word' },
        action: () => this.onExport('docx'),
      });
    }

    if (this.ffExport().pdf) {
      options.push({
        label: this.translate.instant(
          'CHAT.DROPDOWN_MENU.EXPORT_TO_PDF',
        ) as string,
        icon: { type: 'fileType', icon: 'pdf' },
        action: () => this.onExport('pdf'),
      });
    }

    return options;
  });

  public onExport(format: ExportFormat): void {
    if (this.isLoading()) {
      return;
    }

    this.isLoading.set(true);

    const download = DOWNLOADS[format];

    this.analyticsService
      .sendExportChatbotMessageEvent(this.chatId(), format)
      .subscribe();

    this.request(format)
      .pipe(
        tap((response: HttpResponse<Blob>) => {
          if (!response.body) {
            return;
          }
          const responseHeaders = response.headers;

          const contentDisposition = responseHeaders.get('Content-Disposition');
          const filename = getFilenameFromContentDisposition(
            contentDisposition,
            download.fallbackFilename, // if something goes wrong
          );

          // Revokes the blob URL on the next tick: WebKit (iPhone Safari among
          // them) can abort a download whose URL is already gone.
          fromSeriousSystem.triggerDownload(response.body, filename);

          // Track file download event
          this.analyticsService
            .sendFileDownloadedEvent({
              from: download.from,
            })
            .subscribe();
        }),
        catchError((error: unknown) => {
          this.toastrService.error(
            this.translate.instant(download.errorKey) as string,
            undefined,
            {
              closeButton: true,
              disableTimeOut: true,
            },
          );
          console.error(error);
          if (reportsExportFailure(error)) {
            Sentry.captureException(error);
          }
          this.isLoading.set(false);
          return EMPTY;
        }),
        finalize(() => {
          this.isLoading.set(false);
        }),
      )
      .subscribe();
  }

  private request(format: ExportFormat): Observable<HttpResponse<Blob>> {
    switch (format) {
      case 'docx':
        return this.chatsService.chatsControllerExportToDocxV1(
          this.chatUuid(),
          this.messageId(),
          'response',
        );
      case 'pdf':
        return this.chatsService.chatsControllerExportToPdfV1(
          this.chatUuid(),
          this.messageId(),
          'response',
        );
    }
  }
}
